// Composable para manejo de notificaciones
import { useToast } from 'primevue/usetoast'
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '@/constants'

type NotificationType = 'success' | 'error' | 'warn' | 'info'

interface NotificationOptions {
  message: string
  type?: NotificationType
  life?: number
  closable?: boolean
  group?: string
}

export const useNotifications = () => {
  const toast = useToast()

  // Configuración por defecto
  const defaultOptions = {
    life: 3000,
    closable: true
  }

  // Función principal para mostrar notificaciones
  const showNotification = (options: NotificationOptions) => {
    const finalOptions = {
      ...defaultOptions,
      ...options
    }
    
    toast.add({
      severity: finalOptions.type || 'info',
      summary: finalOptions.type === 'success' ? 'Éxito' : finalOptions.type === 'error' ? 'Error' : 'Información',
      detail: finalOptions.message,
      life: finalOptions.life,
      closable: finalOptions.closable
    })
  }

  // Notificaciones de éxito
  const showSuccess = (message: string, options: Partial<NotificationOptions> = {}) => {
    showNotification({
      message,
      type: 'success',
      ...options
    })
  }

  // Notificaciones de error
  const showError = (message: string, options: Partial<NotificationOptions> = {}) => {
    showNotification({
      message,
      type: 'error',
      ...options
    })
  }

  // Notificaciones de advertencia
  const showWarning = (message: string, options: Partial<NotificationOptions> = {}) => {
    showNotification({
      message,
      type: 'warn',
      ...options
    })
  }

  // Notificaciones de información
  const showInfo = (message: string, options: Partial<NotificationOptions> = {}) => {
    showNotification({
      message,
      type: 'info',
      ...options
    })
  }

  // Notificaciones predefinidas para operaciones comunes
  const notifications = {
    // Usuarios
    userCreated: () => showSuccess(SUCCESS_MESSAGES.USER_CREATED),
    userUpdated: () => showSuccess(SUCCESS_MESSAGES.USER_UPDATED),
    userDeleted: () => showSuccess(SUCCESS_MESSAGES.USER_DELETED),
    userNotFound: () => showError(ERROR_MESSAGES.NOT_FOUND),
    
    // Mascotas
    petCreated: () => showSuccess(SUCCESS_MESSAGES.PET_CREATED),
    petUpdated: () => showSuccess(SUCCESS_MESSAGES.PET_UPDATED),
    petDeleted: () => showSuccess(SUCCESS_MESSAGES.PET_DELETED),
    petNotFound: () => showError(ERROR_MESSAGES.NOT_FOUND),
    
    // Razas
    breedCreated: () => showSuccess(SUCCESS_MESSAGES.BREED_CREATED),
    breedUpdated: () => showSuccess(SUCCESS_MESSAGES.BREED_UPDATED),
    breedDeleted: () => showSuccess(SUCCESS_MESSAGES.BREED_DELETED),
    breedNotFound: () => showError(ERROR_MESSAGES.NOT_FOUND),
    
    // Tipos de animales
    animalTypeCreated: () => showSuccess(SUCCESS_MESSAGES.ANIMAL_TYPE_CREATED),
    animalTypeUpdated: () => showSuccess(SUCCESS_MESSAGES.ANIMAL_TYPE_UPDATED),
    animalTypeDeleted: () => showSuccess(SUCCESS_MESSAGES.ANIMAL_TYPE_DELETED),
    animalTypeNotFound: () => showError(ERROR_MESSAGES.NOT_FOUND),
    
    // Autenticación
    passwordChanged: () => showSuccess(SUCCESS_MESSAGES.PASSWORD_CHANGED),
    profileUpdated: () => showSuccess(SUCCESS_MESSAGES.PROFILE_UPDATED),
    
    // Errores comunes
    unauthorized: () => showError(ERROR_MESSAGES.UNAUTHORIZED),
    forbidden: () => showError(ERROR_MESSAGES.FORBIDDEN),
    serverError: () => showError(ERROR_MESSAGES.SERVER_ERROR),
    networkError: () => showError(ERROR_MESSAGES.NETWORK_ERROR),
    validationError: () => showError(ERROR_MESSAGES.VALIDATION_ERROR),
    sessionExpired: () => showError(ERROR_MESSAGES.SESSION_EXPIRED)
  }

  // Manejar errores de API basados en código de estado
  const handleApiError = (error: any) => {
    const status = error.response?.status
    const message = error.response?.data?.error || error.message

    switch (status) {
      case 400:
        showError(message || ERROR_MESSAGES.VALIDATION_ERROR)
        break
      case 401:
        notifications.unauthorized()
        break
      case 403:
        notifications.forbidden()
        break
      case 404:
        showError(message || ERROR_MESSAGES.NOT_FOUND)
        break
      case 422:
        showError(message || ERROR_MESSAGES.VALIDATION_ERROR)
        break
      case 500:
        notifications.serverError()
        break
      default:
         if (error.code === 'NETWORK_ERROR' || !error.response) {
           notifications.networkError()
         } else {
           showError(message || ERROR_MESSAGES.SERVER_ERROR)
         }
    }
  }

  // Mostrar confirmación
  const showConfirmation = (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const confirmed = window.confirm(message)
      resolve(confirmed)
    })
  }

  return {
    // Métodos principales
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // Notificaciones predefinidas
    notifications,
    
    // Utilidades
    handleApiError,
    showConfirmation
  }
}